import {
    Component,
    DestroyRef,
    ElementRef,
    Injector,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { NgComponentOutlet, NgStyle } from '@angular/common';
import { DialogInfo, DialogManager, DialogRef } from './dialog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentService } from '../../../../public-api';
import { dialogFade } from './dialog.animation';

/**
 * Componente que é usado internamente para criar diálogos modais.
 *
 * Este componente não precisa ser utilizado diretamente pelo desenvolvedor.
 * Ele é usado internamente para criar um diálogo modal com um componente específico,
 * informado pelo desenvolvedor.
 *
 */
@Component({
    selector: 'ai-dialog',
    standalone: true,
    templateUrl: './dialog.component.html',
    styleUrls: ['./dialog.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgComponentOutlet, NgStyle],
    animations: [dialogFade],
})
export class Dialog {
    public readonly width = signal<string>('auto');
    public readonly height = signal<string>('auto');
    public readonly minWidth = signal<string>('auto');
    public readonly minHeight = signal<string>('auto');
    public readonly maxWidth = signal<string>('auto');
    public readonly maxHeight = signal<string>('auto');
    public readonly escapeCloses = signal<boolean>(true);

    protected readonly visible = signal<boolean>(false);
    protected readonly component = signal<any>(null);
    protected readonly compInjector: Injector;
    protected inputs: any | null = null;

    private dialogRef: DialogRef | null = null;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly dialogManager: DialogManager,
        private readonly destroyRef: DestroyRef,
        private readonly injector: Injector,
        private readonly documentService: DocumentService,
    ) {
        this.compInjector = Injector.create({
            providers: [
                {
                    provide: DialogInfo,
                    useFactory: () =>
                        new DialogInfo(
                            this.getDialogRef().id,
                            this.getDialogRef().data,
                        ),
                },
            ],
            parent: this.injector,
            name: 'DialogInjector',
        });

        if (this.escapeCloses()) {
            this.documentService
                .onKeyDownEscape()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event: Event | null) => {
                    if (event) {
                        this.close();
                    }
                });
        }
    }

    private getDialogRef(): DialogRef {
        return this.dialogRef!;
    }

    public setInputs(inputs: any | null = null): void {
        this.inputs = inputs ?? {};
    }

    public setDialogRef(dialogRef: DialogRef): void {
        this.dialogRef = dialogRef;
    }

    public setComponent(component: any): void {
        this.component.set(component);
    }

    public show(): void {
        this.visible.set(true);
    }

    public hide(): void {
        this.visible.set(false);
    }

    protected close(): void {
        if (this.dialogRef) {
            this.dialogManager.close(this.dialogRef!.id);
        }
    }
}
